import { useEffect, useRef, useState } from "react";
import { Wrench, Plus, Trash, UploadSimple, Copy, ArrowSquareIn, FloppyDisk, X, CaretDown, CaretUp, ArrowUp, PencilSimple, Minus, Plus as PlusIcon, Info, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser } from "@/lib/api";
import { copyText } from "@/lib/io";
import YTRepliesCard from "@/components/YTRepliesCard";
import YTRepliesConsoleCard from "@/components/YTRepliesConsoleCard";
import YTRepliesRateLimitsCard from "@/components/YTRepliesRateLimitsCard";
import { readStaging, writeStaging } from "@/lib/stagingBuffer";
import ResizeHint from "@/components/ResizeHint";
import { ConfirmModal, PromptModal } from "@/components/PromptModals";
import DocSaveDialog from "@/components/DocSaveDialog";
import SplitAndPushModal, { needsSplit } from "@/components/SplitAndPushModal";
import TextSearch from "@/components/TextSearch";
import AiBuddy from "@/components/AiBuddy";
import YouTubeTool from "@/components/YouTubeTool";
import BoostButton, { activeBoostAllowsSinglePass, activeBoostChunkSize } from "@/components/BoostButton";
import WorkbenchMacros from "@/components/WorkbenchMacros";
import CommandHideControl from "@/components/CommandHideControl";
import CustomCommandsModal from "@/components/CustomCommandsModal";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import LLMGuardWrap from "@/components/LLMGuardWrap";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import { useTrialKeepPrompt } from "@/lib/trial";

// Feb 28, 2026 — Background-job workbench runner. The hosting preview
// ingress kills any HTTP/1.1 connection at 60s, even with live streaming
// chunks. Opus 4.7 on heavy inputs needs 80-120s, so we queue the work
// server-side and poll for the result every ~1.5s — same pattern Refinery
// uses and that's been rock-solid.
async function runWorkbenchJob({ slot1, slot2, slot3, command_id, silo_id, onProgress, target_output_chars }) {
  // POST kicks off the job and returns the id immediately.
  const start = await api.post("/workbench/run/job", { slot1, slot2, slot3, command_id, silo_id, target_output_chars });
  const jobId = start.data?.job_id;
  if (!jobId) throw new Error("No job_id returned from server");
  // Poll every 1.5s. Cap total wait at 30 minutes — Opus 4.7 producing
  // 100k-150k chars of output takes 10-15 min at its native ~40 tok/sec
  // rate, plus headroom for slow networks.
  const POLL_MS = 1500;
  const MAX_POLLS = Math.ceil((30 * 60 * 1000) / POLL_MS);
  for (let i = 0; i < MAX_POLLS; i++) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, POLL_MS));
    // eslint-disable-next-line no-await-in-loop
    const poll = await api.get(`/workbench/run/job/${jobId}`);
    const status = poll.data?.status;
    if (onProgress) {
      try { onProgress({ status, model: poll.data?.model, provider: poll.data?.provider, seconds: ((i + 1) * POLL_MS) / 1000 }); } catch { /* swallow UI errors */ }
    }
    if (status === "done") return poll.data.output || "";
    if (status === "error") {
      const err = new Error(poll.data.error || "Workbench job failed");
      err.response = { status: 502, data: { detail: poll.data.error || "Workbench job failed" } };
      throw err;
    }
  }
  throw new Error("Workbench job timed out after 10 minutes");
}

// Jun 2026 — turn a raw model id ("gemini-2.5-pro") into a friendly label
// ("Gemini 2.5 Pro") for the "Processing through …" line. Falls back to null
// so callers can default to the platform model.
function prettyModelName(m) {
  if (!m) return null;
  return m
    .replace(/^gemini-/, "Gemini ")
    .replace(/^gpt-/, "GPT-")
    .replace(/^claude-/, "Claude ")
    .replace(/-preview$/, "")
    .replace(/-/g, " ")
    .replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
}

function Slot({ index, value, onChange, onUpload, uploading, active, onToggleActive, rows, onRowsChange }) {
  const fileRef = useRef(null);
  const taRef = useRef(null);

  // Listen for manual textarea resize on ANY of the three slots and broadcast the
  // new row count to the parent so the other two slots sync. Earlier iter only
  // Slot 3 carried the drag handle but the user wanted "any slot resizes all
  // three together" — so all three are now `resize-y` and broadcasting is
  // group-coordinated through onRowsChange.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta || !onRowsChange) return;
    const ro = new ResizeObserver(() => {
      try {
        const lineH = parseFloat(getComputedStyle(ta).lineHeight) || 16;
        const padT = parseFloat(getComputedStyle(ta).paddingTop) || 0;
        const padB = parseFloat(getComputedStyle(ta).paddingBottom) || 0;
        const approxRows = Math.max(3, Math.round((ta.clientHeight - padT - padB) / lineH));
        if (approxRows !== rows) onRowsChange(approxRows);
      } catch { /* noop */ }
    });
    ro.observe(ta);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRowsChange]);

  return (
    <div className={`relative bg-white border hairline rounded-lg p-4 ${active ? "" : "opacity-60"}`} data-testid={`slot-${index}`} data-slot-active={active ? "true" : "false"}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-medium text-sm text-[#1C1B1A] shrink-0 flex items-center gap-1.5">
          Slot {index}
          {!active && (
            <span className="text-[9px] uppercase tracking-[0.18em] bg-[#FDF1ED] text-[#C16E5A] px-1.5 py-0.5 rounded" data-testid={`slot-${index}-inactive-badge`}>inactive</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-1 justify-end min-w-0">
          <span className="text-xs text-[#6B6A68] shrink-0">{(value || "").length.toLocaleString()} chars</span>
          {/* Deactivate / reactivate moved into header (iter-28-bugfix) so the
              native textarea bottom-right resize handle is no longer hijacked. */}
          <button
            type="button"
            onClick={() => onToggleActive(index)}
            data-testid={`slot-${index}-toggle-active`}
            title={active ? "Deactivate — excludes this slot from the next Run" : "Reactivate — includes this slot in the next Run"}
            className={`text-xs w-6 h-6 rounded-full border hairline flex items-center justify-center transition-colors shrink-0 ${active ? "bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A]" : "bg-[#C16E5A] text-white border-[#C16E5A]"}`}
          >
            {active ? <Minus size={12} weight="bold" /> : <PlusIcon size={12} weight="bold" />}
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading || !active} data-testid={`slot-${index}-upload`}
            className="text-xs px-2.5 py-1.5 rounded-md text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1 disabled:opacity-50">
            <UploadSimple size={14} weight="duotone" /> {uploading ? "Extracting…" : "Upload"}
          </button>
          {value && (
            <button onClick={() => onChange("")} data-testid={`slot-${index}-clear`}
              className="text-xs px-2 py-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]">
              <X size={14} />
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])} />
        </div>
      </div>
      {value && active && (
        <div className="mb-2"><TextSearch value={value} textareaRef={taRef} testid={`slot-${index}-search`} /></div>
      )}
      <div className="relative">
        <textarea ref={taRef} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={`slot-${index}-textarea`}
          disabled={!active}
          className={`w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] resize-y ${active ? "" : "cursor-not-allowed select-none"}`}
          placeholder={`Paste content into Slot ${index} or upload a file…`} />
        {/* Diagonal strike-through indicator when the slot is deactivated */}
        {!active && (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
            data-testid={`slot-${index}-strikethrough`}
          >
            <line x1="100" y1="0" x2="0" y2="100" stroke="#C16E5A" strokeWidth="0.6" vectorEffect="non-scaling-stroke" opacity="0.75" />
          </svg>
        )}
      </div>
    </div>
  );
}

export default function Workbench({ siloId, siloName, siloSlug }) {
  useTrialKeepPrompt("min_workbench");
  const [slots, setSlots] = useState({ 1: "", 2: "", 3: "" });
  const [slotActive, setSlotActive] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("workbench_slot_active") || "null");
      if (saved && typeof saved === "object") return { 1: !!saved[1], 2: !!saved[2], 3: !!saved[3] };
    } catch { /* ignore */ }
    return { 1: true, 2: true, 3: true };
  });
  const [slotRows, setSlotRows] = useState(6);
  const [uploading, setUploading] = useState({});
  const [commands, setCommands] = useState([]);
  const [selectedCmdId, setSelectedCmdId] = useState("");
  const [output, setOutput] = useState("");
  const [staging, setStaging] = useState(() => readStaging(siloId));
  // Cached user record for the YouTube Tool (just the has_youtube_key flag is needed).
  // Refreshed when the YouTubeTool reports a key save/clear.
  const [ytUser, setYtUser] = useState(() => {
    try { return getUser() || {}; } catch { return {}; }
  });
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancel) {
          setYtUser(r.data);
          try { setUser(r.data); } catch { /* ignore */ }
        }
      } catch { /* not authenticated yet */ }
    })();
    return () => { cancel = true; };
  }, []);
  const [running, setRunning] = useState(false);
  // Phase B — Creator Pro gating. min_workbench = the required base (Run),
  // output_size = the per-pass size slider. Boost is gated inside BoostButton.
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const runLocked = cpLocked("min_workbench");
  const outputSizeLocked = cpLocked("output_size");
  // Jun 2026 (Randy) — "Clear Workbench" top-right button. Bumping this nonce
  // signals child panels (AI Assistant, YouTube tool) to reset themselves.
  const [childClearNonce, setChildClearNonce] = useState(0);
  // Jul 2026 (Randy) — quick top/bottom nav for the long Workbench page (huge
  // video lists). scrollIntoView works whether the page scrolls on window or a
  // container (same mechanism the macro Follow-along uses).
  const scrollWorkbenchToTop = () => {
    document.querySelector('[data-testid="wb-top-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollWorkbenchToBottom = () => {
    document.querySelector('[data-testid="wb-bottom-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  // ---- Macro record & replay (Jun 2026, Randy) ----
  // mode: idle | recording | playing | paused. Recording buffer + timing live
  // in refs so keystroke-level events don't trigger re-renders.
  const [macroMode, setMacroMode] = useState("idle");
  const [macroRecordCount, setMacroRecordCount] = useState(0);
  const recordingRef = useRef(false);
  const recordBufRef = useRef([]);
  const lastStepAtRef = useRef(0);
  const lastOpRef = useRef(null); // provenance: last Workbench operation run this session (best-effort)
  const macroStepLabel = (type, params) => {
    switch (type) {
      case "yt_fetch": return `Fetch YouTube ${params.kind === "metadata" ? "description/title" : params.kind}`;
      case "yt_transfer": return `Send YouTube data → ${params.target === "staging" ? "Staging" : params.target === "assistant" ? "Assistant" : `Slot ${String(params.target).replace("slot", "")}`}`;
      case "run": return `Run "${params.command_name || "operation"}"`;
      case "set_output_size": return `Set output size → ${params.pct == null ? "Auto" : params.pct + "%"}`;
      case "append_result_to_staging": return "Append result → Staging";
      case "send_result_to_slot": return `Send result → Slot ${params.n}`;
      case "send_staging_to_slot": return `Send staging → Slot ${params.n}`;
      case "pause_for_editing": return "Pause for editing";
      default: return type;
    }
  };
  const recordStep = (type, params = {}) => {
    if (!recordingRef.current) return;
    const buf = recordBufRef.current;
    const now = Date.now();
    const delay = lastStepAtRef.current ? Math.min(8000, Math.max(300, now - lastStepAtRef.current)) : 500;
    // Slider drags collapse to their final value; consecutive edit-pauses collapse to one.
    if (type === "set_output_size" && buf.length && buf[buf.length - 1].type === "set_output_size") {
      buf[buf.length - 1].params = params;
      buf[buf.length - 1].label = macroStepLabel(type, params); // keep label in sync w/ final value
      lastStepAtRef.current = now; return;
    }
    if (type === "pause_for_editing" && buf.length && buf[buf.length - 1].type === "pause_for_editing") {
      lastStepAtRef.current = now; return;
    }
    lastStepAtRef.current = now;
    buf.push({ type, params, label: macroStepLabel(type, params), delay_ms: delay });
    setMacroRecordCount(buf.length);
  };
  // May 25, 2026 — Active Boost state. When set, the next Run uses a
  // power model (Sonnet 4.5, Gemini Pro, GPT-5.2) AND skips chunking
  // when the input fits in the model's context window.
  const [workbenchBoost, setWorkbenchBoost] = useState(null);
  // May 25, 2026 — Top-up modal opened from Boost voucher gate.
  const [topupOpenSiloId, setTopupOpenSiloId] = useState(null);
  // May 25, 2026 — Captured outputs per chunk during a multi-chunk boost
  // run. When the run completes AND boost.auto_prep_redundancy is true,
  // these get loaded into Slot 1/2/3 and the command auto-flips to
  // "Remove boundary redundancy". Cleared at the start of any new run.
  const [boostChunkOutputs, setBoostChunkOutputs] = useState([]);
  const [redundancyArmed, setRedundancyArmed] = useState(false);
  // Jun 2026 — per-pass output-size control (Randy). null = "Auto" (let the
  // model decide per the operation). A number 5-100 forces a target output
  // size for the NEXT run only, then reverts to Auto. The silo's workbench
  // surface output ceiling (max tokens) drives the "use Boost above this" hint.
  const [outputPct, setOutputPct] = useState(null);
  const [wbSurface, setWbSurface] = useState(null); // { model, max_output_tokens }
  // Load THIS silo's workbench surface tune so the slider knows the real model
  // + single-pass output ceiling. Non-fatal — falls back to sensible defaults.
  useEffect(() => {
    if (!siloId) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/surface-tune`);
        const wb = (r.data?.surfaces || []).find((s) => s.surface === "workbench_run");
        if (!cancel && wb) setWbSurface({ model: wb.model, max_output_tokens: wb.max_output_tokens });
      } catch { /* non-fatal */ }
    })();
    return () => { cancel = true; };
  }, [siloId]);

  // Sequential chunked processing — iter-28-bugfix made each slot's offset
  // independent so unequal slot lengths don't break compare runs (the 4231 vs
  // 6035 bug). chunkOffset (single number) kept as a back-compat read of the
  // hydration payload but the source of truth is now `chunkOffsets` (per-slot).
  const [chunkOffset, setChunkOffset] = useState(0);
  const [chunkOffsets, setChunkOffsets] = useState({ 1: 0, 2: 0, 3: 0 });
  const [lockedChunkSize, setLockedChunkSize] = useState(0);
  const [lastChunkRanges, setLastChunkRanges] = useState(null);

  // Jun 2026 (Randy, option A): "Auto stage all" runs YOUR SLOTS through the
  // normal Workbench engine — every chunk, one click — into Results + Staging.
  // It does NOT use the Refinery batch engine and never reads the silo.
  // Jul 2026 (Randy): default ON. Macros also force it ON for the duration of a
  // run (then revert) so any-size video is processed in as many passes as needed
  // — this removes the old "too large for a single pass" skip entirely.
  const [refineryMode, setRefineryMode] = useState(true);

  // Customize dialog state
  const [showCustomize, setShowCustomize] = useState(false);
  // Jun 2026 — command explanation popup (mobile can't hover the dropdown options).
  const [explainCmd, setExplainCmd] = useState(null);

  // Modal state (replaces window.confirm/prompt — Mac/Chrome silently blocks repeat dialogs)
  const [confirmState, setConfirmState] = useState({ open: false });
  const [promptState, setPromptState] = useState({ open: false });
  const [commitState, setCommitState] = useState({ open: false, text: "", defaultTitle: "" });
  const [splitState, setSplitState] = useState({ open: false, content: "", siloId: "", tier: 2, title: "" });

  const stagingRef = useRef(null);

  // ---- Server-side workbench-state persistence (iter-20) ----
  // Survives tab close, logout, device switch. Strictly silo-isolated by the backend
  // (composite key user_id + silo_id). On siloId change we hydrate from server
  // (fast cache: sessionStorage is still primed so UX never sees a flash of empty).
  // Saves are debounced 800ms after last edit. Remote updates always win on mount.
  const [hydrating, setHydrating] = useState(true);
  const initialHydrationRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef({});

  // Hydrate from server on mount AND whenever siloId changes.
  useEffect(() => {
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/workbench-state`);
        if (cancelled) return;
        const s = r.data || {};
        setStaging(s.staging || "");
        setOutput(s.output || "");
        setSlots({ 1: s.slot1 || "", 2: s.slot2 || "", 3: s.slot3 || "" });
        if (s.slot_active) setSlotActive({ 1: !!s.slot_active["1"], 2: !!s.slot_active["2"], 3: !!s.slot_active["3"] });
        setChunkOffset(Number(s.chunk_offset || 0));
        // Per-slot offsets (iter-28). Back-compat: if server only has the old
        // single chunk_offset, fan it out to all 3 so the user picks up where
        // they left off without losing work.
        const co = s.chunk_offsets || {};
        const fallback = Number(s.chunk_offset || 0);
        setChunkOffsets({
          1: Number(co["1"] ?? fallback),
          2: Number(co["2"] ?? fallback),
          3: Number(co["3"] ?? fallback),
        });
        setLockedChunkSize(Number(s.locked_chunk_size || 0));
        setLastChunkRanges(s.last_chunk_ranges || null);
        setSlotRows(Number(s.slot_rows || 6));
        // Mirror to sessionStorage so cross-tab + Operations recall keep working.
        writeStaging(siloId, s.staging || "");
        lastSavedRef.current = {
          staging: s.staging || "",
          output: s.output || "",
          slot1: s.slot1 || "", slot2: s.slot2 || "", slot3: s.slot3 || "",
          slot_active: s.slot_active || { "1": true, "2": true, "3": true },
          chunk_offset: Number(s.chunk_offset || 0),
          locked_chunk_size: Number(s.locked_chunk_size || 0),
          last_chunk_ranges: s.last_chunk_ranges || null,
          slot_rows: Number(s.slot_rows || 6),
        };
        initialHydrationRef.current = true;
        setHydrating(false);
      } catch {
        // Network blip: keep whatever is in sessionStorage; mark as hydrated so
        // saves can resume.
        initialHydrationRef.current = true;
        setHydrating(false);
      }
    })();
    return () => { cancelled = true; if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);

  // Persist staging to sessionStorage so the Output Recirculation Buffer (Operations
  // tab) can append while the Workbench is unmounted, and cross-tab updates work.
  useEffect(() => { writeStaging(siloId, staging); }, [staging, siloId]);
  useEffect(() => {
    const onExternal = (e) => {
      if (!e?.detail || e.detail.siloId !== siloId) return;
      const next = readStaging(siloId);
      setStaging((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("staging-updated", onExternal);
    return () => window.removeEventListener("staging-updated", onExternal);
  }, [siloId]);

  // Debounced server save: any time staging/output/slots/etc change, we PATCH the
  // diff to the backend after 800ms of quiet. Skip during initial hydration so we
  // don't immediately overwrite the freshly-loaded state with React defaults.
  useEffect(() => {
    if (!initialHydrationRef.current || hydrating) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const current = {
        staging,
        output,
        slot1: slots[1] || "", slot2: slots[2] || "", slot3: slots[3] || "",
        slot_active: { "1": !!slotActive[1], "2": !!slotActive[2], "3": !!slotActive[3] },
        chunk_offset: chunkOffset,  // back-compat single int
        chunk_offsets: { "1": chunkOffsets[1], "2": chunkOffsets[2], "3": chunkOffsets[3] },
        locked_chunk_size: lockedChunkSize,
        last_chunk_ranges: lastChunkRanges,
        slot_rows: slotRows,
      };
      const diff = {};
      for (const k of Object.keys(current)) {
        if (JSON.stringify(current[k]) !== JSON.stringify(lastSavedRef.current[k])) {
          diff[k] = current[k];
        }
      }
      if (Object.keys(diff).length === 0) return;
      try {
        await api.patch(`/silos/${siloId}/workbench-state`, diff);
        lastSavedRef.current = { ...lastSavedRef.current, ...diff };
      } catch { /* swallow — next change will retry */ }
    }, 800);
    return () => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staging, output, slots, slotActive, chunkOffset, chunkOffsets, lockedChunkSize, lastChunkRanges, slotRows, siloId]);

  const askConfirm = (opts) => setConfirmState({ open: true, ...opts });
  const askPrompt = (opts) => setPromptState({ open: true, ...opts });

  const loadCommands = async () => {
    try {
      const r = await api.get("/commands?scope=slots");
      setCommands(r.data);
      setSelectedCmdId((cur) => {
        if (cur && r.data.some((c) => c.id === cur)) return cur;
        const firstGlobal = r.data.find((c) => c.is_global);
        return (firstGlobal || r.data[0])?.id || "";
      });
    } catch { toast.error("Failed to load commands"); }
  };
  useEffect(() => { loadCommands(); }, []);

  // Jun 2026 (Randy) — the YTRE operation menu opens the shared Custom-commands
  // dialog in place now, so nothing jumps the user over to this tab any more.

  // Pick up "Send to Workbench" seed from Library and append to staging area
  // Polls sessionStorage to support cross-tab arrivals while Workbench stays mounted.
  useEffect(() => {
    const consume = () => {
      try {
        const raw = sessionStorage.getItem("workbench_seed");
        if (!raw) return;
        const seed = JSON.parse(raw);
        sessionStorage.removeItem("workbench_seed");
        if (seed?.staging && seed?.content) {
          // ALWAYS append to the END of whatever's already in staging.
          setStaging((s) => (s ? `${s}\n\n${seed.content}` : seed.content));
          setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
          toast.success(`Appended "${seed.title || "document"}" to staging.`);
        } else if (seed?.slot && seed?.content) {
          setSlots((s) => ({ ...s, [seed.slot]: seed.content }));
        }
      } catch {}
    };
    consume();
    const id = setInterval(consume, 750);
    return () => clearInterval(id);
  }, []);

  const onSlotUpload = async (n, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [n]: true }));
    const form = new FormData(); form.append("file", file);
    try {
      const r = await api.post("/workbench/extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      setSlots((s) => ({ ...s, [n]: r.data.text }));
      toast.success(`Loaded ${r.data.char_count.toLocaleString()} chars from ${file.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not extract file"); }
    finally { setUploading((u) => ({ ...u, [n]: false })); }
  };

  const totalChars = (slotActive[1] ? slots[1]?.length || 0 : 0) + (slotActive[2] ? slots[2]?.length || 0 : 0) + (slotActive[3] ? slots[3]?.length || 0 : 0);
  const populatedCount = [1, 2, 3].filter((n) => slotActive[n] && slots[n] && slots[n].trim()).length;
  const populatedSlots = [1, 2, 3].filter((n) => slotActive[n] && slots[n] && slots[n].trim());
  const maxLen = Math.max(0, ...populatedSlots.map((n) => slots[n].length));

  // Persist slot-active state across SiloManage tab switches.
  useEffect(() => {
    try { sessionStorage.setItem("workbench_slot_active", JSON.stringify(slotActive)); } catch { /* ignore */ }
  }, [slotActive]);

  const toggleSlotActive = (n) => {
    setSlotActive((s) => ({ ...s, [n]: !s[n] }));
  };

  const computeChunkSize = (n) => (n === 1 ? 10000 : n === 2 ? 5000 : n === 3 ? 3000 : 0);
  // May 25, 2026 — when boost is armed AND no chunk size has been locked
  // by a prior run, preview the boosted chunk size in the footer so the
  // user can see the boost actually rewrote the plan.
  const totalInputCharsForFooter = populatedSlots.reduce((acc, n) => acc + (slots[n] || "").length, 0);
  const boostedChunkSizeForFooter = activeBoostChunkSize(workbenchBoost, totalInputCharsForFooter);
  const boostSinglePassForFooter = activeBoostAllowsSinglePass(workbenchBoost, totalInputCharsForFooter);
  const liveChunkSize = lockedChunkSize
    || (boostSinglePassForFooter ? Math.max(totalInputCharsForFooter, 1) : null)
    || boostedChunkSizeForFooter
    || computeChunkSize(populatedCount);

  // iter-28-bugfix: each populated slot iterates INDEPENDENTLY. A slot is
  // "done" when its own offset >= its own length. The job is "complete" only
  // when every active populated slot has reached its end. Inactive or empty
  // slots are ignored. Single shared chunkOffset is kept in sync as the MIN
  // active offset for back-compat (some UI bits still use it).
  const isFirstChunk = chunkOffsets[1] === 0 && chunkOffsets[2] === 0 && chunkOffsets[3] === 0;
  const slotIsDone = (n) => !slotActive[n] || !slots[n] || !slots[n].trim() || (chunkOffsets[n] || 0) >= (slots[n]?.length || 0);
  const isComplete = lockedChunkSize > 0 && populatedSlots.length > 0 && populatedSlots.every(slotIsDone);

  const slotRange = (n) => {
    if (!slotActive[n]) return null;
    const len = slots[n]?.length || 0;
    const off = chunkOffsets[n] || 0;
    if (off >= len) return null;
    const end = Math.min(off + liveChunkSize, len);
    return { start: off + 1, end, total: len };
  };

  // Jun 2026 — output-size slider math. Single-pass output ceiling comes from
  // THIS silo's workbench surface max-tokens (~4 chars/token).
  const surfaceCeilingChars = (wbSurface?.max_output_tokens ? wbSurface.max_output_tokens * 4 : 16000);
  const surfaceModelLabel = prettyModelName(wbSurface?.model) || "Gemini 2.5 Pro";
  // Chars of NEW (not-yet-processed) content the NEXT pass will actually
  // condense, per active slot (excludes the re-fed 10% overlap). Full passes
  // ≈ chunk size; the LAST pass is just whatever's left (smaller). The
  // output-size % is applied to THIS — so the last pass targets, e.g., 20% of
  // the 3,232 remaining, NOT 20% of a full 10k. (Randy, Jun 8 2026.)
  const nextPassNewChars = populatedSlots.reduce((acc, n) => {
    const len = slots[n]?.length || 0;
    const off = chunkOffsets[n] || 0;
    if (off >= len) return acc;
    return acc + Math.min(liveChunkSize, len - off);
  }, 0);
  const isPartialPass = nextPassNewChars > 0 && nextPassNewChars < totalChars;
  const estimatedOutputChars = outputPct == null ? null : Math.max(1, Math.round((nextPassNewChars || totalChars) * (outputPct / 100)));
  const exceedsCeiling = estimatedOutputChars != null && estimatedOutputChars > surfaceCeilingChars;
  // Jun 2026 — per-command output-size preset. Picking an operation starts the
  // slider at THAT command's default (Auto if none). Keyed on the command + its
  // default so a manual slider drag never re-triggers a reset.
  const activeCmd = commands.find((c) => c.id === selectedCmdId);
  const activeDefaultPct = activeCmd ? (activeCmd.default_output_pct ?? null) : null;
  useEffect(() => {
    setOutputPct(activeDefaultPct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCmdId, activeDefaultPct]);

  const startOver = () => {
    setChunkOffset(0);
    setChunkOffsets({ 1: 0, 2: 0, 3: 0 });
    setLockedChunkSize(0);
    setLastChunkRanges(null);
    setOutput("");
  };

  useEffect(() => {
    if (lockedChunkSize > 0 && computeChunkSize(populatedCount) !== lockedChunkSize) startOver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populatedCount]);

  const run = async () => {
    if (populatedCount === 0) return toast.error("Fill at least one slot.");
    const selectedCmd = commands.find((c) => c.id === selectedCmdId);
    if (!selectedCmd) return toast.error("Select an operation first.");
    recordStep("run", {
      command_id: selectedCmd.id,
      command_name: selectedCmd.name,
      // Jul 2026 (Randy) — capture the armed Boost (model + output target) so a
      // recorded macro replays on the SAME power model. Editable later.
      boost: workbenchBoost?.target_model ? {
        target_model: workbenchBoost.target_model,
        target_provider: workbenchBoost.target_provider || null,
        target_max_tokens: workbenchBoost.target_max_tokens || null,
        desired_output_chars: workbenchBoost.desired_output_chars || null,
      } : null,
    });

    // Jun 2026 (Randy, option A) — "Auto stage all" runs YOUR SLOTS through the
    // normal Workbench engine, every chunk in one shot, into Results + Staging.
    // No Refinery job, no chunk-range tags, no silo reads.
    if (refineryMode) {
      return runAllSlots(selectedCmd);
    }

    if (isComplete) return toast.error("All chunks processed. Click 'Start over' to restart.");
    // May 25, 2026 — Safety nudge when the user has chunks loaded for a
    // redundancy pass but the boost has lapsed/been cancelled. Running
    // a 256k-char merge on a default 10k-chunk model would chop the
    // first slot mid-thought. One-shot confirm so we don't nag forever.
    if (redundancyArmed && !workbenchBoost) {
      askConfirm({
        title: "Run boundary-redundancy unboosted?",
        message: "You loaded chunks for a Remove-boundary-redundancy pass, but Boost is no longer armed. Without boost this will chunk the merge at 10k chars — likely truncating slot 1 mid-sentence. Re-arm boost first, or run anyway?",
        confirmLabel: "Run anyway",
        onConfirm: () => { setRedundancyArmed(false); doRun(selectedCmd); },
      });
      return;
    }
    if (selectedCmd.name === "Compare" && populatedCount < 2) {
      askConfirm({
        title: "Compare with one slot?",
        message: "Compare works best with 2+ slots. Run anyway?",
        confirmLabel: "Run",
        onConfirm: () => doRun(selectedCmd),
      });
      return;
    }
    return doRun(selectedCmd);
  };

  // Jun 2026 (Randy, option A) — "Auto stage all": run every chunk of every
  // active slot through the SAME Workbench engine (background job + poll),
  // back to back, in one click. Each clean result streams into Staging (and
  // the combined text shows in Results). No Refinery card, no "[Slot N · chars
  // X-Y]" tags, no silo reads — purely your slot content. The output-size
  // slider applies per pass exactly like a manual Run.
  const runAllSlots = async (selectedCmd) => {
    const totalInputChars = populatedSlots.reduce((acc, n) => acc + (slots[n] || "").length, 0);
    const singlePass = activeBoostAllowsSinglePass(workbenchBoost, totalInputChars);
    const boostedChunkSize = activeBoostChunkSize(workbenchBoost, totalInputChars);
    const chunkSize = singlePass
      ? Math.max(totalInputChars, 1)
      : boostedChunkSize
        ? boostedChunkSize
        : (lockedChunkSize || computeChunkSize(populatedCount));
    const OVERLAP_PCT = 0.1;
    const offsets = {};
    populatedSlots.forEach((n) => { offsets[n] = 0; });
    const remaining = () => populatedSlots.filter((n) => offsets[n] < (slots[n] || "").length);
    const totalPasses = Math.max(1, ...populatedSlots.map((n) => Math.ceil(((slots[n] || "").length || 1) / chunkSize)));

    setRunning(true);
    setOutput("");
    let combined = "";
    let passNum = 0;
    try {
      while (remaining().length > 0) {
        passNum += 1;
        const chunks = { 1: null, 2: null, 3: null };
        let passNewChars = 0;
        for (const n of populatedSlots) {
          const full = slots[n] || "";
          const off = offsets[n];
          if (off >= full.length) continue;
          const startCursor = off > 0 ? Math.max(0, off - Math.floor(chunkSize * OVERLAP_PCT)) : 0;
          chunks[n] = full.slice(startCursor, off + chunkSize);
          passNewChars += Math.min(chunkSize, full.length - off);
        }
        const targetOut = outputPct == null ? null : Math.max(1, Math.round(passNewChars * (outputPct / 100)));
        setOutput(`Auto-staging… pass ${passNum} of ${totalPasses}.`);
        // eslint-disable-next-line no-await-in-loop
        const outputText = await runWorkbenchJob({
          slot1: chunks[1], slot2: chunks[2], slot3: chunks[3],
          command_id: selectedCmd.id,
          silo_id: siloId,
          target_output_chars: targetOut,
          onProgress: ({ status, seconds }) => {
            if (status === "running") setOutput(`Auto-staging… pass ${passNum} of ${totalPasses} · ${Math.round(seconds)}s elapsed.`);
          },
        });
        if (outputText && outputText.trim()) {
          combined = combined ? `${combined}\n\n${outputText}` : outputText;
          setStaging((s) => (s ? `${s}\n\n${outputText}` : outputText));
        }
        for (const n of populatedSlots) {
          if (chunks[n] != null) offsets[n] = Math.min((slots[n] || "").length, offsets[n] + chunkSize);
        }
      }
      setOutput(combined || "");
      if (!lockedChunkSize) setLockedChunkSize(chunkSize);
      setChunkOffsets((co) => {
        const next = { ...co };
        populatedSlots.forEach((n) => { next[n] = (slots[n] || "").length; });
        return next;
      });
      setOutputPct(selectedCmd?.default_output_pct ?? null);
      toast.success(`Auto-staged all · ${combined.length.toLocaleString()} chars across ${passNum} pass${passNum === 1 ? "" : "es"}.`);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.code === "SILO_VOUCHER_EMPTY") {
        macroRunErrorRef.current = { code: "VOUCHER", message: det.message || "Silo voucher is empty." };
        toast.error(det.message || "Silo voucher is empty.");
        setTopupOpenSiloId(det.silo_id || siloId);
      } else {
        macroRunErrorRef.current = { code: "RUN_FAILED", message: (typeof det === "string" ? det : (det?.message || "Auto-stage run failed — please run it again.")) };
        toast.error(typeof det === "string" ? det : (det?.message || "Auto-stage run failed — please run it again."));
      }
    } finally {
      setRunning(false);
    }
  };

  const doRun = async (selectedCmd) => {
    lastOpRef.current = selectedCmd?.name || lastOpRef.current;
    // May 25, 2026 — Boost-aware chunk sizing. If a Boost session is
    // armed for workbench_run:
    //   • Input fits the model's context → single pass (one chunk = full input)
    //   • Input is bigger → use the boosted model's BIG chunk size (e.g.
    //     200k chars for Sonnet) so we still process in 2-3 big chunks
    //     instead of 37 small 10k chunks. Massive quality + speed win.
    const totalInputChars = populatedSlots.reduce((acc, n) => acc + (slots[n] || "").length, 0);
    const singlePass = activeBoostAllowsSinglePass(workbenchBoost, totalInputChars);
    const boostedChunkSize = activeBoostChunkSize(workbenchBoost, totalInputChars);
    const chunkSize = singlePass
      ? Math.max(totalInputChars, 1)
      : boostedChunkSize
        ? boostedChunkSize
        : (lockedChunkSize || computeChunkSize(populatedCount));
    // 10% overlap between consecutive chunks of the SAME slot — prevents
    // cutting an axiom or definition in half across chunk boundaries.
    const OVERLAP_PCT = 0.1;
    const sliceFor = (n) => {
      // Inactive slots are excluded from the run entirely.
      if (!slotActive[n]) return null;
      const full = slots[n] || "";
      if (!full.trim()) return null;
      const off = chunkOffsets[n] || 0;
      if (off >= full.length) return null;  // this slot is already done
      // Overlap only on subsequent chunks.
      const startCursor = off > 0 ? Math.max(0, off - Math.floor(chunkSize * OVERLAP_PCT)) : 0;
      return full.slice(startCursor, off + chunkSize);
    };
    const chunks = { 1: sliceFor(1), 2: sliceFor(2), 3: sliceFor(3) };
    const passInputChars = (chunks[1]?.length || 0) + (chunks[2]?.length || 0) + (chunks[3]?.length || 0);
    // Jun 2026 (Randy) — size the target against the NEW content this pass will
    // process (excludes the re-fed 10% overlap), NOT the full chunk size. So the
    // LAST pass targets, e.g., 20% of the 3,232 chars left — not 20% of 10k —
    // keeping the condense ratio consistent across full and final passes.
    const passNewChars = populatedSlots.reduce((acc, n) => {
      if (!slotActive[n]) return acc;
      const full = slots[n] || "";
      const off = chunkOffsets[n] || 0;
      if (off >= full.length || !full.trim()) return acc;
      return acc + Math.min(chunkSize, full.length - off);
    }, 0);
    const targetOut = outputPct == null ? null : Math.max(1, Math.round((passNewChars || passInputChars) * (outputPct / 100)));
    const ranges = {};
    populatedSlots.forEach((n) => { const r = slotRange(n); if (r) ranges[n] = r; });

    setRunning(true); setOutput("");
    // If this is the very first chunk of a multi-chunk Boost run with
    // auto-prep enabled, reset the captured chunks list. (We capture
    // each chunk's output as it returns, then trigger the slot-load +
    // command-flip when the run completes.)
    const isFirstOfBoostRun = workbenchBoost?.auto_prep_redundancy && isFirstChunk;
    if (isFirstOfBoostRun) {
      setBoostChunkOutputs([]);
      setRedundancyArmed(false);
    }
    try {
      // Feb 28, 2026 — Path-select: heavy / boosted runs use the
      // background-job runner (queues server-side, polls every 1.5s)
      // because the hosting preview ingress kills any HTTP connection
      // at 60s. Quick Gemini-Pro runs on small payloads keep the existing
      // fast path so they're snappy.
      // Jun 2026 — ALWAYS use the background-job runner. Gemini 2.5 Pro is a
      // slow "thinking" model; even a single 10k chunk can run past the
      // preview's 60s connection cutoff and 502. The job runner polls for the
      // result so the timeout can never kill a run. (Same path Boost uses.)
      const useJob = true;
      let outputText;
      if (useJob) {
        outputText = await runWorkbenchJob({
          slot1: chunks[1], slot2: chunks[2], slot3: chunks[3],
          command_id: selectedCmd.id,
          silo_id: siloId,
          target_output_chars: targetOut,
          onProgress: ({ status, seconds }) => {
            // Update the visible result line with a heartbeat so the user
            // knows the boost is grinding away (avoids the "is anything
            // happening?" anxiety on long Opus runs).
            if (status === "running") {
              setOutput(`Working… ${Math.round(seconds)}s elapsed. Larger passes can take a minute or two.`);
            }
          },
        });
        setOutput(outputText);
      } else {
        const r = await api.post("/workbench/run", {
          slot1: chunks[1], slot2: chunks[2], slot3: chunks[3],
          command_id: selectedCmd.id,
          silo_id: siloId,
          target_output_chars: targetOut,
        });
        outputText = r.data.output;
        setOutput(outputText);
      }
      if (!outputText || !outputText.trim()) {
        // Defense-in-depth: backend retries + 502s on empty now, but never
        // fake a "Done" or advance the cursor if nothing actually came back.
        toast.error("The AI returned an empty response — please run this pass again.");
        return;
      }
      if (!lockedChunkSize) setLockedChunkSize(chunkSize);
      // May 25, 2026 — Capture each boost-pass chunk's output. We push
      // to the running list synchronously here so the final check
      // below sees the freshest value. Also append to staging
      // immediately so a browser crash mid-run doesn't lose work
      // (Randy's resilience point, May 25, 2026).
      let capturedChunks = boostChunkOutputs;
      if (workbenchBoost?.auto_prep_redundancy) {
        capturedChunks = [...boostChunkOutputs, outputText];
        setBoostChunkOutputs(capturedChunks);
        const chunkIdx = capturedChunks.length;
        const stagingTag = `--- Boost chunk ${chunkIdx} (${outputText.length.toLocaleString()} chars) ---\n${outputText}`;
        setStaging((s) => (s ? `${s}\n\n${stagingTag}` : stagingTag));
      } else if (workbenchBoost) {
        // Jun 2026 — Plain Boost run (auto-prep off). Mirror the output into
        // the staging area too (not just the Results pane) so a Boost result
        // is never stranded. The Results pane still shows it for review; this
        // guarantees it also lands in staging the way Randy expects.
        const stagingTag = `--- Boost output (${outputText.length.toLocaleString()} chars) ---\n${outputText}`;
        setStaging((s) => (s ? `${s}\n\n${stagingTag}` : stagingTag));
      }
      // Advance ONLY the slots that actually contributed content this pass.
      // Done slots stay parked at their length so isComplete settles correctly.
      setChunkOffsets((co) => {
        const next = { ...co };
        for (const n of [1, 2, 3]) {
          if (chunks[n] != null) {
            const len = (slots[n] || "").length;
            next[n] = Math.min(len, (co[n] || 0) + chunkSize);
          }
        }
        // Mirror the lowest active offset onto the back-compat single int.
        const active = [1, 2, 3].filter((n) => slotActive[n] && (slots[n] || "").trim().length > 0);
        if (active.length) setChunkOffset(Math.min(...active.map((n) => next[n])));
        return next;
      });
      setLastChunkRanges(ranges);
      // Jun 2026 — the output-size nudge is one-time: revert to THIS command's
      // default after a successful run (Randy's spec).
      setOutputPct(selectedCmd?.default_output_pct ?? null);
      toast.success(`Done · ${(outputText || "").length.toLocaleString()} chars`);

      // May 25, 2026 — Auto-prep boundary-redundancy follow-up.
      // After EVERY chunk we check: is the run complete (every active
      // populated slot's offset has reached its end) AND did the user
      // ask for auto-prep? If yes: load the captured chunk outputs
      // into Slots 1/2/3 (overflow to Staging) and flip the command.
      if (workbenchBoost?.auto_prep_redundancy && capturedChunks.length > 1) {
        // Compute the new offsets we just set above to test completion.
        const futureOffsets = { ...chunkOffsets };
        for (const n of [1, 2, 3]) {
          if (chunks[n] != null) {
            const len = (slots[n] || "").length;
            futureOffsets[n] = Math.min(len, (chunkOffsets[n] || 0) + chunkSize);
          }
        }
        const runComplete = populatedSlots.every((n) => {
          if (!slotActive[n]) return true;
          const len = (slots[n] || "").length;
          return !len || (futureOffsets[n] || 0) >= len;
        });
        if (runComplete) {
          // Find the "Remove boundary redundancy" command (seeded
          // globally by the backend). If it's missing for any reason
          // we still load slots so the user can pick it manually.
          const redCmd = commands.find((c) => /remove\s+boundary\s+redundancy/i.test(c.name));
          // May 25, 2026 — PRESERVE ORIGINALS. Before overwriting any
          // slot with chunk outputs, push the current slot contents to
          // the TOP of the staging buffer with clear labels. Without
          // this the user's source file (often the only copy in the
          // app) gets clobbered by chunk outputs and they have nothing
          // to re-run from. Concern raised by Randy on May 25, 2026.
          const originalsSnapshot = [1, 2, 3]
            .filter((n) => (slots[n] || "").length > 0)
            .map((n) => `--- ORIGINAL SOURCE (pre-Boost) · Slot ${n} ·  ${(slots[n] || "").length.toLocaleString()} chars ---\n${slots[n]}`)
            .join("\n\n");
          if (originalsSnapshot) {
            setStaging((s) => (s ? `${originalsSnapshot}\n\n${s}` : originalsSnapshot));
          }
          // Load up to 3 chunks into slots; chunks 4+ get appended to
          // staging so nothing is lost. Slot's "active" flag is set so
          // each loaded slot participates in the next run.
          // Also append ALL chunks (including 1–3) to staging in order,
          // so the user has a clean "full assembled draft" in staging
          // while the slots hold the same content pre-split for the
          // redundancy pass.
          const firstThree = capturedChunks.slice(0, 3);
          const rest = capturedChunks.slice(3);
          setSlots((cur) => {
            const next = { ...cur };
            firstThree.forEach((txt, i) => { next[i + 1] = txt; });
            // Empty any unused slot so we don't carry stale input.
            if (firstThree.length < 3) {
              for (let i = firstThree.length + 1; i <= 3; i++) next[i] = "";
            }
            return next;
          });
          setSlotActive({ 1: firstThree.length >= 1, 2: firstThree.length >= 2, 3: firstThree.length >= 3 });
          // Chunks were already streamed to staging during the run with
          // "Boost chunk N" labels (May 25, 2026 resilience change).
          // No second append here — would duplicate.
          // Rewind chunk progress so the redundancy pass starts fresh.
          setChunkOffsets({ 1: 0, 2: 0, 3: 0 });
          setChunkOffset(0);
          setLockedChunkSize(0);
          setOutput("");
          if (redCmd) {
            setSelectedCmdId(redCmd.id);
            setRedundancyArmed(true);
            toast.success(
              `Loaded ${firstThree.length} chunks into slots and appended all ${capturedChunks.length} to staging. Operation set to "Remove boundary redundancy" — keep boost armed and hit Run.`,
              { duration: 9000 },
            );
          } else {
            toast.success(
              `Loaded ${firstThree.length} chunks into slots and appended all ${capturedChunks.length} to staging. The "Remove boundary redundancy" command is missing — pick a command manually and run.`,
              { duration: 9000 },
            );
          }
        }
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      // Feb 2026 — Empty silo voucher on a non-Gemini route. Pop the
      // top-up modal instead of a dead-end toast.
      if (status === 402 && detail?.code === "SILO_VOUCHER_EMPTY") {
        toast.error(detail.message || "Silo voucher is empty.");
        setTopupOpenSiloId(detail.silo_id || siloId);
      }
      else if (status === 404) { await loadCommands(); toast.error(detail || "That command no longer exists. List refreshed — pick again."); }
      else if (status === 502) toast.error(typeof detail === "string" && detail ? detail : "The AI provider is temporarily busy. Please try again in a moment.");
      else toast.error(detail || "Workbench run failed");
    } finally { setRunning(false); }
  };

  // Output / staging actions
  const sendOutputToSlot = (n) => { if (!output) return; recordStep("send_result_to_slot", { n }); setSlots((s) => ({ ...s, [n]: output })); toast.success(`Output sent to Slot ${n}.`); };
  const sendStagingToSlot = (n) => { if (!staging) return; recordStep("send_staging_to_slot", { n }); setSlots((s) => ({ ...s, [n]: staging })); toast.success(`Staging sent to Slot ${n}.`); };

  const appendToStaging = () => {
    if (!output) return;
    recordStep("append_result_to_staging", {});
    setStaging((s) => (s ? `${s}\n\n${output}` : output));
    toast.success("Appended to staging area.");
  };

  const clearWorkbench = () => {
    askConfirm({
      title: "Clear the whole Workbench?",
      message: "This clears all three slots, the staging area, the Result, the AI Assistant conversation, and the YouTube tool's video URL + output. This can't be undone.",
      danger: true,
      confirmLabel: "Clear everything",
      onConfirm: () => {
        setSlots({ 1: "", 2: "", 3: "" });
        setSlotActive({ 1: true, 2: true, 3: true });
        setStaging("");
        setOutput("");
        startOver();
        setChildClearNonce((n) => n + 1);
        toast.success("Workbench cleared.");
      },
    });
  };
  const handleCopy = async (text) => {
    const ok = await copyText(text);
    toast[ok ? "success" : "error"](ok ? "Copied to clipboard." : "Could not copy.");
  };
  const handleCommit = (text, defaultTitle) => {
    if (!text?.trim()) return;
    setCommitState({ open: true, text, defaultTitle: defaultTitle || "" });
  };
  // Commit is now handled entirely by <DocSaveDialog> in commit mode (dup-name
  // guard + Hide + Split-&-Push handoff). Kept same-silo pinned to the current silo.

  const builtIn = commands.filter((c) => c.is_global);
  const custom = commands.filter((c) => !c.is_global);

  // Jun 2026 (Randy): nothing the platform generates may travel with the
  // content. A "-- STAGING AREA --" banner used to be prepended to every
  // export and commit, which baked it into silo files and then into answers.
  // Exports carry the text only. Titles are still derived from the first ~10
  // words so the user doesn't have to type one (still editable in the modal).
  const firstTenWords = (text) => {
    const words = (text || "").trim().split(/\s+/).filter(Boolean).slice(0, 10);
    return words.join(" ").slice(0, 120) || "Staged content";
  };

  const ActionRow = ({ text, testidPrefix, defaultTitle, sendTo, variant, onClear, clearTestId }) => {
    const isStaging = variant === "staging";
    const exportText = text;
    const exportTitle = isStaging ? firstTenWords(text) : defaultTitle;
    // Shared compact button style — small on mobile so all buttons fit in ONE
    // single row under the staging/result area; roomier on desktop.
    const btnBase = "text-[11px] sm:text-sm px-2.5 sm:px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap";
    return (
      // Desktop: buttons spread out with Clear centered (flex-1 spacers).
      // Mobile: spacers collapse, everything packs into a single scrollable row.
      <div className="mt-3 flex items-center gap-1.5 sm:gap-2 flex-nowrap overflow-x-auto" data-testid={`${testidPrefix}-actions`}>
        {/* Far-left primary: Commit to Silo (staging) / Append to staging (results) */}
        {isStaging ? (
          <button onClick={() => handleCommit(exportText, exportTitle)} data-testid="stg-commit"
            className="text-[11px] sm:text-sm px-2.5 sm:px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap">
            <FloppyDisk size={14} />
            <span className="hidden sm:inline">Commit to silo</span><span className="sm:hidden">Silo</span>
          </button>
        ) : (
          <button onClick={appendToStaging} data-testid="wb-append-staging"
            className="text-[11px] sm:text-sm px-2.5 sm:px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap">
            <ArrowUp size={14} weight="bold" />
            <span className="hidden sm:inline">Append to staging</span><span className="sm:hidden">Stage</span>
          </button>
        )}
        {/* Copy */}
        <button onClick={() => handleCopy(exportText)} data-testid={`${testidPrefix}-copy`} className={btnBase}>
          <Copy size={14} /> <span>Copy</span>
        </button>
        {/* Desktop spacer — centers Clear */}
        <div className="hidden sm:block flex-1" />
        {/* Clear (center) */}
        <button onClick={onClear} data-testid={clearTestId}
          className="text-[11px] sm:text-sm px-2.5 sm:px-4 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap">
          <Trash size={14} /> <span>Clear</span>
        </button>
        {/* Desktop spacer — centers Clear */}
        <div className="hidden sm:block flex-1" />
        {/* Send to Slot 1/2/3 — icon + "Slot N" on desktop, just the number on mobile */}
        {[1, 2, 3].map((n) => (
          <button key={n} onClick={() => sendTo(n)} data-testid={`${testidPrefix}-send-slot-${n}`} className={btnBase}>
            <ArrowSquareIn size={14} />
            <span className="hidden sm:inline">Slot {n}</span><span className="sm:hidden">{n}</span>
          </button>
        ))}
      </div>
    );
  };

  // ---- Macro engine bridge (Jun 2026, Randy) ----
  // Fresh-state refs so the macro player reads current values (not stale
  // closures) when it drives the Workbench imperatively between steps.
  const slotsRef = useRef(slots); slotsRef.current = slots;
  const slotActiveRef = useRef(slotActive); slotActiveRef.current = slotActive;
  const stagingStateRef = useRef(staging); stagingStateRef.current = staging;
  const outputRef = useRef(output); outputRef.current = output;
  const runningRef = useRef(running); runningRef.current = running;
  const selectedCmdIdRef = useRef(selectedCmdId); selectedCmdIdRef.current = selectedCmdId;
  const commandsRef = useRef(commands); commandsRef.current = commands;
  const ytApiRef = useRef(null);
  const macroBusRef = useRef({});
  // Feb 17, 2026 (Randy pt-macro-selected) — bridge for the YouTube Tool's
  // new "Run selected Macro" button. WorkbenchMacros registers its selected-
  // macro helpers onto this ref every render; YouTubeTool reads them on click.
  const workbenchMacroApiRef = useRef(null);
  // Jul 2026 (Randy) — captures a run failure the swallow-and-toast paths hide,
  // so the macro engine can HALT on a voucher-empty (retry won't help) but keep
  // going on a transient/empty-output pass. Mirrors refineryMode into a ref so
  // the bus routes to the right engine without waiting on a re-render.
  const macroRunErrorRef = useRef(null);
  const refineryModeRef = useRef(refineryMode); refineryModeRef.current = refineryMode;
  macroBusRef.current = {
    getState: () => ({ slots: slotsRef.current, staging: stagingStateRef.current, output: outputRef.current, running: runningRef.current, selectedCmdId: selectedCmdIdRef.current, autoStage: refineryModeRef.current }),
    selectOperation: (id) => setSelectedCmdId(id),
    setOutputSize: (pct) => setOutputPct(pct),
    // Jul 2026 — with Auto-Stage forced ON for macros, route Run through the
    // multi-pass engine (runAllSlots) so any-size video is processed fully. Then
    // surface a captured run error so the macro can classify skip-vs-halt.
    runOperation: async () => {
      const cmd = commandsRef.current.find((c) => c.id === selectedCmdIdRef.current);
      if (!cmd) throw new Error("No operation selected.");
      macroRunErrorRef.current = null;
      if (refineryModeRef.current) await runAllSlots(cmd);
      else await doRun(cmd);
      if (macroRunErrorRef.current) {
        const e = new Error(macroRunErrorRef.current.message || "the run failed");
        e.code = macroRunErrorRef.current.code;
        throw e;
      }
    },
    getAutoStage: () => refineryModeRef.current,
    setAutoStage: (on) => setRefineryMode(!!on),
    // Arm a persistent Boost for the WHOLE macro run (not "just this run", which
    // dies after the first call). Reverted/cancelled when the macro ends.
    armBoostForMacro: async (boostCfg, runCount = 1) => {
      if (!boostCfg?.target_model) return null;
      const capC = Math.max(500, Math.min(5000, Math.round(500 * Math.max(1, runCount))));
      const r = await api.post("/boost/start", {
        surface: "workbench_run",
        target_model: boostCfg.target_model,
        target_provider: boostCfg.target_provider,
        target_max_tokens: boostCfg.target_max_tokens || 8192,
        duration_min: 240,
        cost_cap_cents: capC,
        just_this_run: false,
        surface_silo_id: siloId || null,
        desired_output_chars: boostCfg.desired_output_chars || 0,
      });
      setWorkbenchBoost({ ...r.data });
      return r.data;
    },
    cancelBoostForMacro: async () => {
      try {
        const a = await api.get("/boost/active");
        const mine = (a.data?.sessions || []).find((s) => s.surface === "workbench_run");
        if (mine) await api.post(`/boost/${mine.id}/cancel`);
      } catch { /* ignore */ }
      setWorkbenchBoost(null);
    },
    appendResultToStaging: () => appendToStaging(),
    sendResultToSlot: (n) => sendOutputToSlot(n),
    sendStagingToSlot: (n) => sendStagingToSlot(n),
    getCommands: () => commandsRef.current,
    // Batch macro guard (Jul 2 2026, Randy): would the CURRENT slot contents
    // force the operation into multiple passes? Uses the same single-pass chunk
    // budget the Workbench itself uses (1 slot 10k / 2 slots 5k / 3 slots 3k).
    // If so, the batch skips that video instead of under-processing it.
    wouldNeedMultiplePasses: () => {
      const s = slotsRef.current || {};
      const act = slotActiveRef.current || {};
      const populated = [1, 2, 3].filter((n) => act[n] && (s[n] || "").trim());
      if (populated.length === 0) return false;
      const budget = populated.length === 1 ? 10000 : populated.length === 2 ? 5000 : 3000;
      const maxLen = Math.max(...populated.map((n) => (s[n] || "").length));
      return maxLen > budget;
    },
    // Batch mode: programmatic (no-modal) commit of the staging area. Returns the
    // created doc id so a batch can offer "Undo all". Throws on oversize/API error.
    commitStagingDirect: async ({ tier, title, operation }) => {
      const text = stagingStateRef.current;
      if (!text || !text.trim()) throw new Error("staging was empty — nothing to commit");
      if (needsSplit(text, tier)) throw new Error(`result too large for Tier ${tier} — commit this one manually (Split & Push)`);
      const res = await api.post(`/silos/${siloId}/docs/paste`, { title, content: text, tier, source_surface: "workbench", source_operation: operation || lastOpRef.current || null });
      return res.data?.id || null;
    },
    clearStaging: () => setStaging(""),
    deleteDoc: async (docId) => { await api.delete(`/silos/${siloId}/docs/${docId}`); },
    ytApiRef,
  };
  const macroApi = { recordingRef, record: recordStep, registerApi: (a) => { ytApiRef.current = a; } };

  return (
    <div data-testid="workbench-tab">
      <div data-testid="wb-top-anchor" className="scroll-mt-24" />
      <div className="flex items-center gap-2 mb-1">
        <Wrench size={20} weight="duotone" className="text-[#4A5D4E]" />
        <h2 className="font-serif-display text-3xl text-[#1C1B1A]">The Recursive Workbench</h2>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Feb 2026 (Randy pt-yt-macros-jump) — quick scroll to the Macros
              section further down the page. */}
          <button
            onClick={() => {
              const el = document.getElementById("workbench-macros-anchor");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="workbench-jump-macros"
            title="Jump to Macros"
            className="text-xs px-2 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1.5">
            <CaretDown size={14} weight="bold" /> Macros
          </button>
          <button onClick={clearWorkbench} data-testid="workbench-clear-all"
            title="Clear all slots, staging, result, the AI Assistant conversation, and the YouTube tool's URL + output"
            className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#C16E5A] hover:bg-[#FDF1ED] hover:border-[#C16E5A] flex items-center gap-1.5 shrink-0">
            <Broom size={14} weight="duotone" /> Clear Workbench
          </button>
        </div>
      </div>
      <p className="text-sm text-[#6B6A68] mb-6">Three slots, one operation, infinite refinement. Stage promising results, layer them, then commit to your silo. The home for anything that needs to see multiple files at once — compare, merge, cross-reference and more.</p>

      {/* Slots 1/2/3 are part of the Minimum Workbench — gate EACH slot
          individually so each gets its OWN frameless glimmer (Randy, Jul 2026:
          no giant square wrapping all three). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((n) => (
          <LockGate key={n} locked={runLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("min_workbench")} block>
            <Slot index={n} value={slots[n]} uploading={!!uploading[n]}
              active={slotActive[n]}
              onToggleActive={toggleSlotActive}
              rows={slotRows}
              onRowsChange={setSlotRows}
              onChange={(v) => { recordStep("pause_for_editing", {}); setSlots((s) => ({ ...s, [n]: v })); }}
              onUpload={(f) => onSlotUpload(n, f)} />
          </LockGate>
        ))}
      </div>

      <div
        data-testid="wb-operation-card"
        className="mt-5 bg-white border hairline rounded-lg p-4 flex flex-wrap items-center gap-3 scroll-mt-24"
      >
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <span className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Operation</span>
          <div className="relative flex-1">
            <select data-testid="workbench-command" value={selectedCmdId} onChange={(e) => {
                const id = e.target.value;
                setSelectedCmdId(id);
                const c = commands.find((x) => x.id === id);
                // Mobile (no hover) can't see the per-option tooltip — pop the
                // explanation in the center of the screen on selection instead.
                if (c && typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: none)").matches) {
                  setExplainCmd(c);
                }
              }}
              className="appearance-none w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
              {/* Jun 2026 (Randy) — his own operations sit ABOVE the built-ins,
                  newest first. The order comes from GET /commands, so the YTRE
                  dropdown inherits it without a second decision. */}
              {custom.length > 0 && (
                <optgroup label="Your custom commands">
                  {custom.map((c) => <option key={c.id} value={c.id} title={c.instructions}>{c.name}</option>)}
                </optgroup>
              )}
              <optgroup label="Global (built-in)">
                {builtIn.map((c) => <option key={c.id} value={c.id} title={c.instructions}>{c.name}</option>)}
              </optgroup>
            </select>
            <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
          </div>
          <button
            type="button"
            onClick={() => { const c = commands.find((x) => x.id === selectedCmdId); if (c) setExplainCmd(c); }}
            data-testid="workbench-command-info"
            title="What does this operation do?"
            aria-label="Explain this operation"
            className="shrink-0 w-9 h-9 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center justify-center text-[#6B6A68]"
          >
            <Info size={16} />
          </button>
          {/* May 25, 2026 — Per-user hide/unhide control. Hide button
              mutes the currently-selected GLOBAL command from this
              user's dropdown. "Unhide" submenu appears (with the
              count) when this user has any hidden. */}
          <CommandHideControl
            scope="slots"
            selectedCmd={commands.find((c) => c.id === selectedCmdId)}
            onReload={loadCommands}
          />
        </div>
        <button onClick={() => setShowCustomize(true)} data-testid="workbench-customize"
          className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
          <Plus size={14} /> Customize
        </button>
        <div className="text-xs text-[#6B6A68]">{populatedCount}/3 slots · {totalChars.toLocaleString()} chars total</div>
        {/* iter-29: Refinery toggle — when ON, hitting Run kicks off a backend
            job that auto-iterates ALL chunks in the background and streams
            every chunk's output into your staging area below. No silo write
            happens; you decide what to commit. */}
        <label className="text-xs text-[#6B6A68] flex items-center gap-1.5 cursor-pointer" title="Walk away while every chunk runs. Results stream into your staging area — you decide what to commit to the silo.">
          <input type="checkbox" checked={refineryMode} onChange={(e) => setRefineryMode(e.target.checked)}
            data-testid="workbench-refinery-toggle" />
          <span className={refineryMode ? "text-[#4A5D4E] font-medium" : ""}>Auto stage all</span>
        </label>
        {lockedChunkSize > 0 && !isComplete && !refineryMode && (
          <button onClick={() => askConfirm({ title: "Reset chunk position?", message: "This will rewind to the beginning of the slots.", confirmLabel: "Reset", onConfirm: startOver })}
            data-testid="workbench-start-over"
            className="text-xs px-3 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A]">
            Start over
          </button>
        )}
        <div className="ml-auto">
          <LockGate locked={runLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("min_workbench")}>
            <LLMGuardWrap surface="workbench_run" siloId={siloId}>
              <button onClick={run} disabled={running || populatedCount === 0 || (isComplete && !refineryMode)} data-testid="workbench-run"
                className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
                {running ? "Processing…" : refineryMode ? "Auto stage all" : isComplete ? "All chunks processed" : isFirstChunk ? "Run" : "Process Next Chunk"}
              </button>
            </LLMGuardWrap>
          </LockGate>
        </div>
        {/* May 25, 2026 — Boost button. Sits next to Run. Lets the user
            arm a power model (Sonnet / Pro / GPT-5.2) for the next run.
            When the model has enough context to swallow the whole input
            in one shot, we auto-skip chunking on the client. */}
        <BoostButton
          surface="workbench_run"
          gateSku="wb_boost"
          inputChars={populatedSlots.reduce((acc, n) => acc + (slots[n] || "").length, 0)}
          onBoostChange={({ active, boost }) => setWorkbenchBoost(active ? boost : null)}
          testidPrefix="workbench-boost"
          siloId={siloId}
          onRequestTopup={(sid) => setTopupOpenSiloId(sid)}
        />
      </div>

      {populatedCount > 0 && (
        <div className="mt-3 text-xs bg-[#F0F1EE] border hairline rounded-md px-4 py-3" data-testid="chunk-progress">
          {isComplete ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[#4A5D4E]">
                ✓ All chunks processed across every slot. Stage what you need, then start fresh.
              </div>
              <button onClick={startOver} data-testid="workbench-complete-restart"
                className="text-xs px-4 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">
                Start over
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-[#6B6A68] uppercase tracking-[0.18em] text-[10px]">
                {isFirstChunk ? "Next pass — chunk size locks once you run" : "Next pass"} · {liveChunkSize.toLocaleString()} chars/slot · 10% overlap between chunks
                {workbenchBoost && !lockedChunkSize && (
                  <span className="ml-2 normal-case tracking-normal text-[#7A5200]">
                    ⚡ Boost {boostSinglePassForFooter ? "single-pass" : `rewrote chunks (was 10k)`}
                  </span>
                )}
              </div>
              {populatedSlots.map((n) => {
                const r = slotRange(n);
                return r ? (
                  <div key={n} className="text-[#1C1B1A] font-mono flex items-center gap-2" data-testid={`slot-${n}-progress-active`}>
                    <span>Slot {n}: chars {r.start.toLocaleString()}–{r.end.toLocaleString()} of {r.total.toLocaleString()}</span>
                    <button onClick={() => askPrompt({
                      title: `Skip ahead in Slot ${n}`,
                      message: `Jump to a specific character position in Slot ${n} (currently at ${(chunkOffsets[n] || 0).toLocaleString()} of ${r.total.toLocaleString()}). Useful when you want to start refining at, say, the beginning of chapter 4.`,
                      placeholder: `e.g., ${Math.floor(r.total / 2)}`,
                      defaultValue: String(chunkOffsets[n] || 0),
                      onSubmit: (val) => {
                        const target = Math.max(0, Math.min(r.total, parseInt(val, 10) || 0));
                        setChunkOffsets((co) => ({ ...co, [n]: target }));
                        if (!lockedChunkSize) setLockedChunkSize(computeChunkSize(populatedCount));
                        toast.success(`Slot ${n} cursor → char ${target.toLocaleString()}.`);
                      },
                    })} data-testid={`slot-${n}-skip-ahead`}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] hover:text-[#1C1B1A] hover:underline">
                      skip ahead
                    </button>
                  </div>
                ) : (
                  <div key={n} className="text-[#4A5D4E] font-mono italic" data-testid={`slot-${n}-progress-done`}>Slot {n}: ✓ done</div>
                );
              })}
              {/* Jun 2026 — per-pass output-size slider (Randy). Auto by default
                  (model decides per the operation); slide to FORCE a target size
                  for the next run only. One-time — reverts to Auto after a run. */}
              <div className="mt-2.5 pt-2.5 border-t border-[#E0DED8]" data-testid="output-size-control">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[#6B6A68] uppercase tracking-[0.18em] text-[10px]" data-testid="wb-output-size-label">Output size</span>
                  <span className="text-[10px] text-[#1C1B1A] tabular-nums" data-testid="output-size-readout">
                    {outputPct == null
                      ? "Auto · model decides"
                      : `~${outputPct}% · ≈ ${(estimatedOutputChars || 0).toLocaleString()} chars${isPartialPass ? " this pass" : ""}`}
                  </span>
                </div>
                <LockGate locked={outputSizeLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("output_size")} block>
                <input
                  type="range" min={0} max={100} step={5}
                  value={outputPct == null ? 0 : outputPct}
                  onChange={(e) => { const v = parseInt(e.target.value, 10); recordStep("set_output_size", { pct: v === 0 ? null : v }); setOutputPct(v === 0 ? null : v); }}
                  disabled={running}
                  data-testid="output-size-slider"
                  className="w-full mt-1.5 accent-[#4A5D4E] cursor-pointer disabled:opacity-50"
                />
                </LockGate>
                <div className="flex items-center justify-between text-[9px] text-[#9A9892] uppercase tracking-[0.14em] mt-0.5">
                  <span>Auto</span><span>50%</span><span>100%</span>
                </div>
                {exceedsCeiling && (
                  <div className="mt-1.5 text-[10px] text-[#7A5200] bg-[#FFF7E6] border border-[#F0D9A0] rounded px-2 py-1" data-testid="output-size-boost-hint">
                    ⚡ Above what {surfaceModelLabel} can write in one pass (~{surfaceCeilingChars.toLocaleString()} chars). Use Boost to achieve larger results.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Staging area — Iter160m (Randy pt-siloart-loosetext-sweep) —
          `<div>` staging labels are LOOSE TEXT on the canvas. `.silo-loose`
          opts them into the whitetext/blacktext rules despite being bare
          divs (which are excluded by default to avoid wrapper false-hits). */}
      <div className="mt-8" data-testid="staging-area">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#1C1B1A] silo-loose" data-testid="wb-staging-label">Staging area</div>
          <div className="flex items-center gap-2 flex-1 max-w-xs">
            {staging && <TextSearch value={staging} textareaRef={stagingRef} testid="staging-search" />}
          </div>
          <div className="text-xs text-[#1C1B1A] silo-loose">{staging.length.toLocaleString()} chars</div>
        </div>
        <textarea ref={stagingRef} value={staging} onChange={(e) => { recordStep("pause_for_editing", {}); setStaging(e.target.value); }}
          rows={Math.min(14, Math.max(6, Math.ceil((staging.length || 0) / 200)))}
          data-testid="staging-textarea"
          className="w-full bg-white border hairline rounded-lg px-4 py-3 font-mono text-xs leading-relaxed text-[#1C1B1A] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          placeholder="Stage promising results here. Append from the Result area, edit freely, then send to a slot or commit." />
        {staging && (
          <ActionRow
            text={staging}
            testidPrefix="stg"
            defaultTitle="Staged content"
            variant="staging"
            sendTo={sendStagingToSlot}
            clearTestId="stg-clear"
            onClear={() => askConfirm({ title: "Clear staging area?", message: "Clears all content from staging.", danger: true, confirmLabel: "Clear", onConfirm: () => setStaging("") })}
          />
        )}
      </div>

      {/* Result */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#1C1B1A] silo-loose" data-testid="result-label">Result · {(output || "").length.toLocaleString()} chars</div>
          <div className="flex items-center gap-3">
            {lastChunkRanges && Object.keys(lastChunkRanges).length > 0 && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]" data-testid="result-chunk-meta">
                ↳ from{" "}
                {Object.entries(lastChunkRanges).map(([n, r], i, arr) => (
                  <span key={n}>Slot {n} {r.start.toLocaleString()}–{r.end.toLocaleString()}{i < arr.length - 1 ? " · " : ""}</span>
                ))}
              </div>
            )}
            {output && (
              <button onClick={appendToStaging} data-testid="wb-append-staging-top"
                className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
                <ArrowUp size={12} weight="bold" /> Append to staging
              </button>
            )}
          </div>
        </div>
        <div
          className="bg-white border hairline rounded-lg p-5 min-h-[200px] max-h-[1200px] resize-y overflow-auto whitespace-pre-wrap text-sm text-[#1C1B1A] font-mono leading-relaxed relative"
          data-testid="workbench-output"
          style={{ resize: "vertical" }}
        >
          {running ? <span className="text-[#6B6A68]">Processing…</span>
            : output ? output : <span className="text-[#6B6A68]">Run an operation to see results here.</span>}
          <ResizeHint title="Drag bottom-right to resize the result pane" />
        </div>
        {output && (
          <ActionRow
            text={output}
            testidPrefix="wb"
            defaultTitle="Workbench output"
            variant="results"
            sendTo={sendOutputToSlot}
            clearTestId="wb-output-clear"
            onClear={() => askConfirm({ title: "Clear results?", message: "Clears the Result area. Staging area is untouched.", danger: true, confirmLabel: "Clear", onConfirm: () => setOutput("") })}
          />
        )}
      </div>

      {/* AI Assistant — classic OpenAI helper for brainstorming. NOT silo-RAG. */}
      <div className="mt-8" data-testid="ai-buddy-mount-workbench">
        <AiBuddy
          surface="workbench"
          clearRequest={childClearNonce}
          siloId={siloId}
          siloSlug={siloSlug}
          showSiloControls
          title="AI Assistant"
          subtitle="Brainstorm"
          emptyHint="Ask Assistant anything. Brainstorm ideas, expand thoughts, create outlines, append to your staging area above. Your personal Silo awaits!"
          inputPlaceholder="This Assistant is connected to the world, not your silo. Ask it anything!"
          onCommitToSilo={(text) => handleCommit(text, "")}
          onAppendToStaging={(text) => {
            setStaging((s) => (s ? s + "\n\n" + text : text));
            toast.success("Appended Assistant reply to staging.");
            setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
          }}
        />
      </div>

      {/* YouTube Tool — pull metadata, transcripts, comments. Per-creator BYOK key. */}
      <div className="mt-6" data-testid="youtube-tool-mount">
        <YouTubeTool
          siloId={siloId}
          clearRequest={childClearNonce}
          macroApi={macroApi}
          workbenchMacroApiRef={workbenchMacroApiRef}
          user={ytUser}
          onUserUpdated={(u) => { setYtUser(u); try { setUser(u); } catch { /* ignore */ } }}
          onAppendToStaging={(text) => {
            setStaging((s) => (s ? s + "\n\n" + text : text));
            toast.success(`Appended to staging · ${text.length.toLocaleString()} chars`);
            setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
          }}
          onSendToSlot={(n, text) => {
            if (!slotActive[n]) {
              toast.error(`Slot ${n} is deactivated — reactivate it first.`);
              return;
            }
            setSlots((s) => ({ ...s, [n]: text }));
            toast.success(`Sent to Slot ${n} · ${text.length.toLocaleString()} chars`);
          }}
          onSendToAssistant={(text) => {
            // Lazy import — file already imported AiBuddy above
            import("@/components/AiBuddy").then(({ seedAssistant }) => {
              seedAssistant("workbench", text);
              toast.success("Seeded into AI Assistant — open the panel to ask your question.");
            });
          }}
        />
      </div>

      {/* Macros — record & replay a video-processing workflow */}
      <div id="workbench-macros-anchor">
      <WorkbenchMacros
        busRef={macroBusRef}
        ytApiRef={ytApiRef}
        macroApiRef={workbenchMacroApiRef}
        siloId={siloId}
        mode={macroMode}
        setMode={setMacroMode}
        recordingRef={recordingRef}
        recordBufRef={recordBufRef}
        lastStepAtRef={lastStepAtRef}
        recordCount={macroRecordCount}
        setRecordCount={setMacroRecordCount}
      />
      </div>

      {/* Customize — the shared dialog, also opened from the YTRE op menu */}
      {showCustomize && (
        <CustomCommandsModal
          siloId={siloId}
          scope="slots"
          commands={commands}
          onReload={loadCommands}
          onCreated={(c) => setSelectedCmdId(c.id)}
          onClose={() => setShowCustomize(false)}
          requestConfirm={askConfirm}
        />
      )}

      {/* Modals replacing window.confirm/prompt */}
      {explainCmd && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="workbench-command-explain-overlay"
          onClick={() => setExplainCmd(null)}
        >
          <div
            className="modal-safe relative w-full max-w-md bg-[#FDFCF8] border hairline rounded-xl shadow-2xl p-6"
            data-testid="workbench-command-explain-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setExplainCmd(null)}
              data-testid="workbench-command-explain-close"
              aria-label="Close explanation"
              className="absolute top-3 right-3 w-9 h-9 rounded-md border hairline bg-white hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center justify-center text-[#6B6A68]"
            >
              <X size={18} />
            </button>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1.5">What this operation does</div>
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] pr-10" data-testid="workbench-command-explain-name">{explainCmd.name}</h3>
            <p className="text-sm text-[#3A4A3E] mt-3 whitespace-pre-wrap leading-relaxed" data-testid="workbench-command-explain-body">{explainCmd.instructions}</p>
            <div className="mt-4 text-xs text-[#6B6A68] border-t hairline pt-3">
              Default output size:{" "}
              <span className="font-medium text-[#1C1B1A]" data-testid="workbench-command-explain-size">
                {explainCmd.default_output_pct == null ? "Auto · model decides" : `~${explainCmd.default_output_pct}% of the input`}
              </span>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal {...confirmState} onClose={() => setConfirmState({ open: false })} />
      <PromptModal {...promptState} onClose={() => setPromptState({ open: false })} />
      <DocSaveDialog
        open={commitState.open}
        onClose={() => setCommitState({ open: false, text: "", defaultTitle: "" })}
        content={commitState.text}
        sourceSiloId={siloId}
        sourceDoc={null}
        silos={[{ id: siloId, name: siloName || "this silo" }]}
        lockSilo
        defaultTitle={commitState.defaultTitle}
        initialTier={2}
        sourceSurface="impromptu_staging"
        dialogTitle={siloName ? `Commit to "${siloName}"` : "Commit to silo"}
        checkOversize={(content, tier) => needsSplit(content, tier)}
        onOversize={({ title, tier }) => {
          setSplitState({ open: true, content: commitState.text, siloId, tier, title: title || "" });
          setCommitState({ open: false, text: "", defaultTitle: "" });
        }}
        onSaved={() => {}}
      />
      <SplitAndPushModal
        open={splitState.open}
        content={splitState.content}
        defaultTitle={splitState.title}
        siloId={splitState.siloId}
        siloName={siloName}
        initialTier={splitState.tier}
        sourceSurface="impromptu_staging"
        sourceOperation={null}
        onClose={() => setSplitState((s) => ({ ...s, open: false }))}
        onComplete={() => setSplitState((s) => ({ ...s, open: false }))}
      />
      {/* May 25, 2026 — VoucherTopupModal hook for Boost cost-cap gate. */}
      <VoucherTopupModal
        open={!!topupOpenSiloId}
        prefocusedSiloId={topupOpenSiloId}
        onClose={() => setTopupOpenSiloId(null)}
        onSuccess={() => setTopupOpenSiloId(null)}
      />

      {/* Quick-nav (Jul 2026, Randy): two transparent floating carets that stay
          on-screen anywhere on the tab. TOP jumps to the bottom, BOTTOM jumps to
          the top. Inside workbench-tab so they only show here, z-100 (< the macro
          shelf's z-120) so playback covers them. */}
      {/* AI YouTube Comment Replies — locked Creator Pro add-on card (one-time
          SKU, priced from the catalog — never hardcode it). Channel 1
          lives on the Workbench; Channel 2 lives on the Refinery. Static
          visual for now — OAuth + Stripe wiring lands in a later phase.
          (Randy, Jul 2026 — FEATURE_YT_AI_REPLIES_SPEC.md) */}
      <YTRepliesCard channelSlot="channel1" siloId={siloId} />
      {/* Operating console — Randy Jul 2026: shows the daily-use surface
          (drafts, approvals, safety mode, signature, analytics, danger zone)
          in preview mode until OAuth+Stripe wiring lands. */}
      <YTRepliesConsoleCard channelSlot="channel1" siloId={siloId} siloSlug={siloSlug} />
      {/* Rate-limits editor — Randy Jul 2026: per-channel auto-post thresholds
          (posts/day, posts/comment, min spacing). Starts collapsed. */}
      <YTRepliesRateLimitsCard channelSlot="channel1" siloId={siloId} />

      <div data-testid="wb-bottom-anchor" className="h-0" />
      <button
        type="button"
        onClick={scrollWorkbenchToBottom}
        data-testid="workbench-jump-bottom"
        title="Jump to the bottom of the Workbench"
        style={{ top: "calc(7.5rem + env(safe-area-inset-top))" }}
        className="fixed left-1/2 -translate-x-1/2 z-[100] w-[26px] h-[26px] rounded-full bg-transparent border hairline backdrop-blur-sm text-[#4A5D4E] hover:border-[#4A5D4E] active:scale-95 transition-transform flex items-center justify-center"
      >
        <CaretDown size={11} weight="bold" />
      </button>
      <button
        type="button"
        onClick={scrollWorkbenchToTop}
        data-testid="workbench-jump-top"
        title="Jump to the top of the Workbench"
        style={{ bottom: "calc(0.9rem + env(safe-area-inset-bottom))" }}
        className="fixed left-1/2 -translate-x-1/2 z-[100] w-[26px] h-[26px] rounded-full bg-transparent border hairline backdrop-blur-sm text-[#4A5D4E] hover:border-[#4A5D4E] active:scale-95 transition-transform flex items-center justify-center"
      >
        <CaretUp size={11} weight="bold" />
      </button>
    </div>
  );
}
